import { useState } from 'react'
import { fmtUnit } from '@/lib/format-unit'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { PricingItem, CreateLineItemPayload, LineItemCategory } from '@/types'

interface AddLineItemDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onAdd: (payload: CreateLineItemPayload) => void
  pricingItems: PricingItem[]
}

const CATEGORIES: { value: LineItemCategory; label: string }[] = [
  { value: 'labor', label: 'Labor' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'materials', label: 'Materials' },
  { value: 'striping', label: 'Striping' },
  { value: 'misc', label: 'Misc' },
]

const SearchIcon = () => (
  <svg
    className="h-4 w-4 text-muted-foreground"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
)

export function AddLineItemDialog({
  open,
  onOpenChange,
  onAdd,
  pricingItems,
}: AddLineItemDialogProps) {
  const [tab, setTab] = useState<'catalog' | 'custom'>('catalog')
  const [search, setSearch] = useState('')
  const [selectedItem, setSelectedItem] = useState<PricingItem | null>(null)
  const [catalogQty, setCatalogQty] = useState(1)

  // Custom form state
  const [customForm, setCustomForm] = useState({
    description: '',
    category: 'labor' as LineItemCategory,
    quantity: 1,
    unit: 'hr',
    rate: 0,
  })

  const filteredItems = pricingItems.filter(
    (item) =>
      item.name.toLowerCase().includes(search.toLowerCase()) ||
      item.description?.toLowerCase().includes(search.toLowerCase())
  )

  const handleAddFromCatalog = () => {
    if (!selectedItem) return
    onAdd({
      description: selectedItem.name,
      category: (selectedItem.category as LineItemCategory) || 'misc',
      quantity: catalogQty,
      unit: selectedItem.unit,
      rate: selectedItem.rate,
      pricingItemId: selectedItem.id,
    })
    handleClose()
  }

  const handleAddCustom = () => {
    if (!customForm.description || customForm.rate <= 0) return
    onAdd({
      description: customForm.description,
      category: customForm.category,
      quantity: customForm.quantity,
      unit: customForm.unit,
      rate: customForm.rate,
    })
    handleClose()
  }

  const handleClose = () => {
    onOpenChange(false)
    setTab('catalog')
    setSearch('')
    setSelectedItem(null)
    setCatalogQty(1)
    setCustomForm({
      description: '',
      category: 'labor',
      quantity: 1,
      unit: 'hr',
      rate: 0,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Line Item</DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setTab('catalog')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === 'catalog'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            From Catalog
          </button>
          <button
            onClick={() => setTab('custom')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === 'custom'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Custom
          </button>
        </div>

        {/* Catalog Tab */}
        {tab === 'catalog' && (
          <div className="space-y-4 py-4">
            {/* Search */}
            <div className="relative">
              <SearchIcon />
              <Input
                placeholder="Search items..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Items List */}
            <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
              {filteredItems.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No items found
                </div>
              ) : (
                filteredItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setSelectedItem(item)}
                    className={`w-full text-left p-3 hover:bg-accent transition-colors ${
                      selectedItem?.id === item.id ? 'bg-blue-50 border-l-2 border-blue-500' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{item.name}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {fmtUnit(item.unit)}
                        </p>
                      </div>
                      <p className="text-sm font-semibold text-right">
                        ${item.rate.toFixed(2)}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>

            {/* Selected Item Details */}
            {selectedItem && (
              <div className="border rounded-lg p-3 bg-blue-50">
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="catalog-qty" className="text-xs font-medium">
                      Quantity
                    </Label>
                    <Input
                      id="catalog-qty"
                      type="number"
                      value={catalogQty || ''}
                      onChange={(e) => setCatalogQty(parseFloat(e.target.value) || 1)}
                      className="mt-1 h-8 text-sm"
                      min="0.01"
                      step="0.01"
                    />
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Unit Price:</span>
                    <span className="font-semibold">${selectedItem.rate.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold pt-2 border-t">
                    <span>Line Total:</span>
                    <span>${(catalogQty * selectedItem.rate).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={handleClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddFromCatalog}
                disabled={!selectedItem}
                className="flex-1"
              >
                Add Item
              </Button>
            </div>
          </div>
        )}

        {/* Custom Tab */}
        {tab === 'custom' && (
          <div className="space-y-4 py-4">
            {/* Description */}
            <div>
              <Label htmlFor="custom-desc" className="text-sm font-medium">
                Description
              </Label>
              <Input
                id="custom-desc"
                placeholder="e.g., Custom work"
                value={customForm.description}
                onChange={(e) =>
                  setCustomForm({ ...customForm, description: e.target.value })
                }
                className="mt-2"
              />
            </div>

            {/* Category */}
            <div>
              <Label htmlFor="custom-cat" className="text-sm font-medium">
                Category
              </Label>
              <select
                id="custom-cat"
                value={customForm.category}
                onChange={(e) =>
                  setCustomForm({
                    ...customForm,
                    category: e.target.value as LineItemCategory,
                  })
                }
                className="w-full mt-2 px-3 py-2 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Qty, Unit, Rate */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="custom-qty" className="text-sm font-medium">
                  Qty
                </Label>
                <Input
                  id="custom-qty"
                  type="number"
                  value={customForm.quantity || ''}
                  onChange={(e) =>
                    setCustomForm({
                      ...customForm,
                      quantity: parseFloat(e.target.value) || 0,
                    })
                  }
                  className="mt-2"
                  min="0.01"
                  step="0.01"
                />
              </div>
              <div>
                <Label htmlFor="custom-unit" className="text-sm font-medium">
                  Unit
                </Label>
                <Input
                  id="custom-unit"
                  placeholder="hr, day, sq ft"
                  value={customForm.unit}
                  onChange={(e) =>
                    setCustomForm({ ...customForm, unit: e.target.value })
                  }
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="custom-rate" className="text-sm font-medium">
                  Rate
                </Label>
                <div className="relative mt-2">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                    $
                  </span>
                  <Input
                    id="custom-rate"
                    type="number"
                    value={customForm.rate || ''}
                    onChange={(e) =>
                      setCustomForm({
                        ...customForm,
                        rate: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="pl-7"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>

            {/* Line Total Preview */}
            <div className="border rounded-lg p-3 bg-muted/30">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Line Total:</span>
                <span className="font-semibold">
                  ${(customForm.quantity * customForm.rate).toFixed(2)}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={handleClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddCustom}
                disabled={!customForm.description || customForm.rate <= 0}
                className="flex-1"
              >
                Add Item
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
